import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supplierAPI } from '../../api/suppliers';
import { Supplier } from '../../types/entities';
import { Plus, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const SupplierList: React.FC = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      const data = await supplierAPI.getAll();
      setSuppliers(data);
    } catch (error) {
      toast.error(t('suppliers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('suppliers.deleteConfirm'))) return;

    try {
      await supplierAPI.delete(id);
      toast.success(t('suppliers.deleteSuccess'));
      loadSuppliers();
    } catch (error) {
      toast.error(t('suppliers.deleteError'));
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('suppliers.title')}</h1>
        <button
          onClick={() => navigate('/suppliers/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('suppliers.addNew')}
        </button>
      </div>

      <div className="card">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('suppliers.name')}</th>
              <th className="pb-3 table-header">{t('suppliers.phone')}</th>
              <th className="pb-3 table-header">{t('suppliers.address')}</th>
              <th className="pb-3 table-header">{t('common.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr key={supplier.id} className="border-b last:border-0">
                  <td className="py-3 font-medium">{supplier.name}</td>
                  <td className="py-3">{supplier.phone || '-'}</td>
                  <td className="py-3">{supplier.address || '-'}</td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        supplier.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {supplier.status === 'ACTIVE' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(supplier.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
